import { useContext, useEffect, useState, useRef } from "react";
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";

import timerSound from "assets/mp3/timer.mp3";

import bgImage from "assets/images/timer-bg.jpg";
import bgImage2 from "assets/images/bg-image.png";

import { LanguageContext } from "context";
import { useNotification, type NotificationData } from "context/NotificationContext";
import { useSessionContext } from "context/SessionContext";

import { saveLog } from "@shared/api/timer";

const DEFAULT_WORK_MINUTES = 25;
const DEFAULT_BREAK_MINUTES = 5;

// 値の範囲を制限するヘルパー関数
const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

const PomodoroTimer = () => {
  const { t } = useContext(LanguageContext) as any;
  const { showNotification } = useNotification();
  const { session } = useSessionContext();

  const [isRunning, setIsRunning] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [muted, setMuted] = useState(false);
  const [isAwaitingBreakStart, setIsAwaitingBreakStart] = useState(false);

  const [workMinutes, setWorkMinutes] = useState(DEFAULT_WORK_MINUTES);
  const [breakMinutes, setBreakMinutes] = useState(DEFAULT_BREAK_MINUTES);
  
  const [secondsLeft, setSecondsLeft] = useState(workMinutes * 60);

  // タイマーの中で古い値を保持しているのでrefで最新の値を使用するようにする
  const mutedRef = useRef(muted);
  const isBreakRef = useRef(isBreak);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<InstanceType<typeof window.Audio> | null>(null);
  const lastTickRef = useRef<number | null>(null);

  const workMinRef = useRef(workMinutes);
  const breakMinRef = useRef(breakMinutes);

  const [pendingNotification, setPendingNotification] = useState<NotificationData | null>(null);

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const playSound = () => {
    const audio = new window.Audio(timerSound);
    audioRef.current = audio;
    audio.play();
  };

  const stopSound = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
  };

  const startTimer = () => {
    if (isRunning) return;
    setIsRunning(true);
    
    lastTickRef.current = Date.now();

    timerRef.current = setInterval(() => {
      const now = Date.now();
      if (lastTickRef.current === null) lastTickRef.current = now;
      
      const diffMs = now - lastTickRef.current;
      const diffSec = Math.floor(diffMs / 1000);
      
      if (diffSec <= 0) return;
      
      lastTickRef.current += diffSec * 1000;

      setSecondsLeft((prev) => {
        const next = prev - diffSec;
        
        if (next <= 0) {
          clearInterval(timerRef.current!);
          setIsRunning(false);

          if (!isBreakRef.current) {            
            console.log(`not break start::`);
            if (!mutedRef.current) {
              playSound();
            } else {
               console.log(`not break showNotification::`);
              setPendingNotification({
                icon: "check_circle",
                title: t("timer.title"),
                message: t("timer.complete-msg"),
              });
            }
            setIsAwaitingBreakStart(true);
            
            // 最新の設定分数をログに送る
            logPomodoroSession(0, workMinRef.current);
            
            return breakMinRef.current * 60;
          } else {
            console.log(`break start::`);
            stopTimer();
            setTimeout(() => {
              setIsBreak(false);
              setIsAwaitingBreakStart(false);
              console.log(`break reset::`);
            }, 0);
            
            // 最新の設定分数をログに送る
            logPomodoroSession(1, breakMinRef.current);

            if (!mutedRef.current) {
              playSound();
            } else {
              console.log(`break showNotification::`);
              setPendingNotification({
                icon: "check_circle",
                title: t("timer.title"),
                message: t("timer.break-complete-msg"),
              });
            }
            
            return workMinRef.current * 60;
          }
        }
        return next;
      });
    }, 250);
  };

  const startBreak = () => {
    stopSound();
    setIsAwaitingBreakStart(false);
    setIsBreak(true);
    setSecondsLeft(breakMinutes * 60);
    startTimer();
  };

  const stopTimer = () => {
    setIsRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
    lastTickRef.current = null; 
  };

  const resetTimer = () => {
    stopTimer();
    stopSound();
    setIsBreak(false);
    isBreakRef.current = false;
    setIsAwaitingBreakStart(false);
    setSecondsLeft(workMinutes * 60);
  };

  const toggleMute = () => {
    setMuted((prev) => !prev);
  };

  const handleChangeWork = (delta: number) => {
    if (isRunning) return;
    const newVal = clamp(workMinutes + delta, 5, 120);
    setWorkMinutes(newVal);
    // 集中モード待機中なら、表示されている残り時間もすぐに更新する
    if (!isBreak && !isAwaitingBreakStart) {
      setSecondsLeft(newVal * 60);
    }
  };

  const handleChangeBreak = (delta: number) => {
    if (isRunning) return;
    const newVal = clamp(breakMinutes + delta, 1, 30);
    setBreakMinutes(newVal);
    // 休憩モード待機中なら、表示されている残り時間もすぐに更新する
    if (isBreak || isAwaitingBreakStart) {
      setSecondsLeft(newVal * 60);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopSound();
    };
  }, []);

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { isBreakRef.current = isBreak; }, [isBreak]);
  useEffect(() => { workMinRef.current = workMinutes; }, [workMinutes]);
  useEffect(() => { breakMinRef.current = breakMinutes; }, [breakMinutes]);

  useEffect(() => {
    if (pendingNotification) {
      showNotification(pendingNotification, 6000);
      setPendingNotification(null);
    }
  }, [pendingNotification]);

  const logPomodoroSession = async (type: 0 | 1, minutes: number) => {
    try {
      if (!session?.access_token) {
        return;
      }
      await saveLog(session.access_token, type, minutes);
    } catch (error) {
      console.error("ポモドーロログ送信失敗:", error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={{ xs: "auto", md: "calc(100vh - 200px)" }}
        py={8}
        px={2}
        mb={4}
        sx={{
          backgroundImage: `url(${isBreak || isAwaitingBreakStart ? bgImage2 : bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          textAlign: "center",
          width: "100%",
          boxSizing: "border-box", 
          borderRadius: "xl"
        }}
      >
        <MDTypography variant="h3" color="white" mb={1} sx={{ textShadow: "1px 1px 4px rgba(0,0,0,0.6)" }}>
          {isAwaitingBreakStart
            ? t("timer.complete-msg")
            : isBreak
              ? t("timer.break")
              : isRunning
                ? t("timer.work")
                : t("timer.ready") }
        </MDTypography>

        {/* タイマー円形部分 */}
        <MDBox
          sx={{
            width: 260,
            height: 260,
            borderRadius: "50%",
            border: "2px solid white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
            backgroundColor: "rgba(0,0,0,0.2)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}
        >
          <MDTypography variant="h1" color="white" sx={{ textShadow: "2px 2px 4px rgba(0,0,0,0.5)" }}>
            {formatTime(secondsLeft)}
          </MDTypography>
        </MDBox>

        {/* コントロールボタン群 */}
        <MDBox display="flex" justifyContent="center" gap={2} flexWrap="wrap" mb={4}>
          {!isRunning && !isAwaitingBreakStart ? (
            <MDButton variant="gradient" color="light" onClick={startTimer}>
              <Icon>play_arrow</Icon>&nbsp;{t("timer.start-msg")}
            </MDButton>
          ) : isRunning ? (
            <MDButton variant="gradient" color="warning" onClick={stopTimer}>
              <Icon>pause</Icon>&nbsp;{t("timer.stop-msg")}
            </MDButton>
          ) : null}

          {isAwaitingBreakStart && (
            <MDButton variant="gradient" color="success" onClick={startBreak}>
              <Icon>self_improvement</Icon>&nbsp;{t("timer.start-beark")}
            </MDButton>
          )}

          <MDButton variant="gradient" color="error" onClick={resetTimer}>
            <Icon>refresh</Icon>&nbsp;{t("timer.reset")}
          </MDButton>
          <MDButton variant="gradient" color={muted ? "secondary" : "info"} onClick={toggleMute}>
            <Icon>{muted ? "volume_off" : "volume_up"}</Icon>&nbsp;
            {muted ? t("timer.mute") : t("timer.sound")}
          </MDButton>
        </MDBox>

        <MDBox
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
            borderRadius: "16px",
            p: { xs: 2, sm: 3 }, 
            width: "100%",       
            maxWidth: "360px",
            boxSizing: "border-box" 
          }}
        >
          <MDTypography variant="h6" color="white" mb={2} textAlign="center">
            {t("timer.settings") || "設定"}
          </MDTypography>
          
          {/* 中央揃えにしつつ、中のブロックを均等割り（flex={1}）にする */}
          <MDBox display="flex" justifyContent="center" alignItems="center" width="100%" gap={{ xs: 1, sm: 2 }}>
            
            {/* 集中時間の設定 */}
            <MDBox display="flex" flexDirection="column" alignItems="center" flex={1}>
              <MDTypography variant="button" color="white" fontWeight="medium">
                {t("timer.concentration_title") || "集中"}
              </MDTypography>
              <MDBox display="flex" alignItems="center" justifyContent="center" mt={1} gap={1} width="100%">
                <MDButton variant="outlined" color="white" iconOnly circular disabled={isRunning} onClick={() => handleChangeWork(-5)}>
                  <Icon>remove</Icon>
                </MDButton>
                <MDTypography variant="h5" color="white" sx={{ minWidth: "40px", textAlign: "center" }}>
                  {workMinutes}
                </MDTypography>
                <MDButton variant="outlined" color="white" iconOnly circular disabled={isRunning} onClick={() => handleChangeWork(5)}>
                  <Icon>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>

            {/* 休憩時間の設定 */}
            <MDBox display="flex" flexDirection="column" alignItems="center" flex={1}>
              <MDTypography variant="button" color="white" fontWeight="medium">
                {t("timer.break_title") || "休憩"}
              </MDTypography>
              <MDBox display="flex" alignItems="center" justifyContent="center" mt={1} gap={1} width="100%">
                <MDButton variant="outlined" color="white" iconOnly circular disabled={isRunning} onClick={() => handleChangeBreak(-1)}>
                  <Icon>remove</Icon>
                </MDButton>
                <MDTypography variant="h5" color="white" sx={{ minWidth: "40px", textAlign: "center" }}>
                  {breakMinutes}
                </MDTypography>
                <MDButton variant="outlined" color="white" iconOnly circular disabled={isRunning} onClick={() => handleChangeBreak(1)}>
                  <Icon>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>

          </MDBox>
        </MDBox>

      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default PomodoroTimer;